import { ResolvedTaxRate, ResolvedTaxRateLocation } from "../../../domain/invoicing/resolvedTaxRateApi"
import { formatRatePercent, TaxRate } from "../../../domain/invoicing/taxRate"

export const AUTOMATIC_TAX_RATE_OPTION = 'automatic'

export type TaxRateOption = {
  label: string
  value: string
}

export const buildTaxRateOptions = (taxRates: TaxRate[], isAutomaticOffered: boolean): TaxRateOption[] => {
  const manualOptions = taxRates.map(x => ({ label: x.name, value: x.id }))
  if (!isAutomaticOffered) {
    return manualOptions
  }
  return [{ label: 'Calculate Automatically', value: AUTOMATIC_TAX_RATE_OPTION }, ...manualOptions]
}

export const toSelectedTaxRateValue = (selectedTaxRateId: string | null | undefined, isAutomaticOffered: boolean): string | undefined => {
  if (isAutomaticOffered) {
    return selectedTaxRateId ?? AUTOMATIC_TAX_RATE_OPTION
  }
  return selectedTaxRateId ?? undefined
}

export const fromSelectedTaxRateValue = (value: string): string | null => {
  if (value === AUTOMATIC_TAX_RATE_OPTION) {
    return null
  }
  return value
}

export type LocationMismatch = {
  resolvedLocation: string
  propertyLocation: string
}

const cleanPart = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

export const formatTaxLocation = (location: ResolvedTaxRateLocation | null | undefined): string | null => {
  if (!location) {
    return null
  }
  const city = cleanPart(location.city)
  const state = cleanPart(location.state)
  const zip = cleanPart(location.zip)
  const stateAndZip = [state, zip].filter(x => x !== null).join(' ')
  const parts = [city, stateAndZip].filter(x => x)
  if (parts.length === 0) {
    return null
  }
  return parts.join(', ')
}

const normalizeState = (value: string | null | undefined): string | null => {
  return cleanPart(value)?.toUpperCase() ?? null
}

const normalizeZip = (value: string | null | undefined): string | null => {
  const cleaned = cleanPart(value)
  if (cleaned === null) {
    return null
  }
  const beforeDash = cleaned.split('-')[0].trim()
  if (/^\d{9}$/.test(beforeDash)) {
    return beforeDash.slice(0, 5)
  }
  return beforeDash.toUpperCase()
}

const statesConflict = (resolved: string | null, submitted: string | null): boolean => {
  if (resolved === null || submitted === null || resolved === submitted) {
    return false
  }
  return resolved.length === 2 && submitted.length === 2
}

const zipsConflict = (resolved: string | null, submitted: string | null): boolean => {
  return resolved !== null && submitted !== null && resolved !== submitted
}

export const getLocationMismatch = (
  resolved: ResolvedTaxRateLocation | null | undefined,
  submitted: ResolvedTaxRateLocation | null | undefined,
): LocationMismatch | null => {
  if (!resolved || !submitted) {
    return null
  }
  const hasConflict = statesConflict(normalizeState(resolved.state), normalizeState(submitted.state))
    || zipsConflict(normalizeZip(resolved.zip), normalizeZip(submitted.zip))
  if (!hasConflict) {
    return null
  }
  const resolvedText = formatTaxLocation(resolved)
  const submittedText = formatTaxLocation(submitted)
  if (resolvedText === null || submittedText === null) {
    return null
  }
  return { resolvedLocation: resolvedText, propertyLocation: submittedText }
}

export type AutomaticRateDisplay =
  | { mode: 'hidden' }
  | { mode: 'loading' }
  | { mode: 'resolved'; ratePercent: string; jurisdiction: string | null; resolvedLocationText: string | null; locationMismatch: LocationMismatch | null }
  | { mode: 'unresolved' }

type AutomaticRateDisplayInput = {
  isAutomaticSelected: boolean
  isResolvedRateLoading: boolean
  isResolvedRateError: boolean
  resolvedTaxRate: ResolvedTaxRate | undefined
}

export const getAutomaticRateDisplay = ({
  isAutomaticSelected,
  isResolvedRateLoading,
  isResolvedRateError,
  resolvedTaxRate,
}: AutomaticRateDisplayInput): AutomaticRateDisplay => {
  if (!isAutomaticSelected || isResolvedRateError) {
    return { mode: 'hidden' }
  }

  if (isResolvedRateLoading || resolvedTaxRate === undefined) {
    return { mode: 'loading' }
  }

  if (resolvedTaxRate.isResolved) {
    return {
      mode: 'resolved',
      ratePercent: formatRatePercent(resolvedTaxRate.rate),
      jurisdiction: resolvedTaxRate.jurisdiction,
      resolvedLocationText: formatTaxLocation(resolvedTaxRate.resolvedLocation),
      locationMismatch: getLocationMismatch(resolvedTaxRate.resolvedLocation, resolvedTaxRate.submittedLocation),
    }
  }

  return { mode: 'unresolved' }
}

type AutoSelectDefaultInput = {
  isAutomaticOffered: boolean
  isAutomaticAvailabilityKnown: boolean
  hasSelectedTaxRateId: boolean
}

export const shouldAutoSelectDefaultTaxRate = ({
  isAutomaticOffered,
  isAutomaticAvailabilityKnown,
  hasSelectedTaxRateId,
}: AutoSelectDefaultInput): boolean => {
  if (!isAutomaticAvailabilityKnown || isAutomaticOffered) {
    return false
  }
  return !hasSelectedTaxRateId
}
